import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Bot } from 'lucide-react';
import { RxCross2 } from 'react-icons/rx';
import { TemporaryChatContext } from '../../Context/temporaryChatContext';
import { useParams } from 'react-router-dom';
import sessionsService from '../../services/sessionsServices';
import { AuthContext } from '../../Context/authContext';
import { useContextHook } from 'use-context-hook';
import SourcesTab from './Sources';
import 'swiper/css';
import 'swiper/css/navigation';
import { ChatSectionStyled, ChatColumn, SearchResultCol } from './ChatSection.styles';
import ChatMessageItem from './ChatMessageItem';
import SourceDrawer from './SourceDrawer';
import EntityModal from './EntityModal';
import PolyChatHeader from './PolyChatHeader';
import PolyChatInput from './PolyChatInput';
import teamService from '../../services/teamService';
import { formatSessionHistory } from '../../helpers/formatSessionHistory';
import { DEFAULT_COPILOT_ID, DEFAULT_COPILOT_NAME, isDefaultCopilot, toDefaultAwareAgentOption } from '../../constants/defaultCopilot';

function getAgentIconSrc(agent) {
  if (!agent?.icon) return null;
  if (
    typeof agent.icon === 'string' &&
    (agent.icon.startsWith('/') ||
      agent.icon.startsWith('http://') ||
      agent.icon.startsWith('https://') ||
      agent.icon.startsWith('data:'))
  ) {
    if (agent.icon.startsWith('http://') || agent.icon.startsWith('https://')) {
      return agent.icon.split('?')[0].toLowerCase().endsWith('.svg') ? null : agent.icon;
    }
    return agent.icon;
  }
  if (typeof agent.icon === 'string' && agent.icon.startsWith('data:')) return agent.icon;
  if (typeof agent.icon === 'string') return `data:image/png;base64,${agent.icon}`;
  return null;
}

const ChatSection = () => {
  const { user } = useContextHook(AuthContext, v => ({
    user: v.user,
  }));
  const [activeTab, setActiveTab] = useState('Sources');
  const {
    selectedAgent,
    chatMessages,
    setChatmessages,
    setSessionAgent,
    sessionAgent,
    updateAgent,
    isActiveRef,
    setSessionID,
  } = useContext(TemporaryChatContext);
  const [selectedChatSources, setSelectedChatSources] = useState(null);
  const [activeSource, setActiveSource] = useState(null);
  const [activeEntity, setActiveEntity] = useState(null);
  const [teamDetails, setTeamDetails] = useState(null);
  const { id } = useParams();
  const tabs = ['Sources'];
  const hasSession = Boolean(id);

  useEffect(() => {
    const fetchTeamData = async () => {
      if (selectedAgent?.mode === 'team' && selectedAgent?.value) {
        try {
          const res = await teamService?.getTeamDetails(selectedAgent.value);
          setTeamDetails(res?.detail);
        } catch (error) {
          console.error('Failed to fetch team details:', error);
        }
      }
    };

    fetchTeamData();
  }, [selectedAgent]);

  const getSessions = async sessionId => {
    try {
      const aiResp = await sessionsService.getSessionHistory({
        user_id: user?.data?.ID,
        platform_id: 'feedalabs',
        agent_id: 'ferdy',
        session_id: sessionId,
      });
      if (aiResp?.agent_name || aiResp?.agent_id) {
        const sessionAgentOption = toDefaultAwareAgentOption({
          agent_name: aiResp?.agent_name,
          id: aiResp?.agent_id,
          icon: aiResp?.icon,
        });
        setSessionAgent(sessionAgentOption);
        updateAgent(sessionAgentOption);
      }
      const formattedMessages = formatSessionHistory(aiResp?.history);

      setChatmessages(formattedMessages);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (id && isActiveRef.current) {
      getSessions(id);
      setSessionID(id);
    }
    if (!id) {
      // Fresh chat landing (/chat): clear any messages/session left over from a previous chat.
      setChatmessages([]);
      setSessionID(null);
    }
  }, [id]);

  const displayAgent = useMemo(() => {
    const raw = selectedAgent?.label ? selectedAgent : sessionAgent;
    if (!raw) return { ...{ label: DEFAULT_COPILOT_NAME, value: DEFAULT_COPILOT_ID } };
    if (isDefaultCopilot(raw) || String(raw.value) === DEFAULT_COPILOT_ID) {
      return toDefaultAwareAgentOption(raw);
    }
    return raw;
  }, [selectedAgent, sessionAgent]);
  const emptyStateIcon = useMemo(() => getAgentIconSrc(displayAgent), [displayAgent]);
  const emptyTitle = displayAgent?.label || DEFAULT_COPILOT_NAME;

  const sessionAgentIcon = useMemo(() => {
    if (selectedAgent?.mode === 'team') {
      return selectedAgent?.icon;
    }
    if (isDefaultCopilot(sessionAgent) || isDefaultCopilot(selectedAgent)) {
      return getAgentIconSrc(toDefaultAwareAgentOption(sessionAgent || selectedAgent));
    }
    return getAgentIconSrc(sessionAgent);
  }, [selectedAgent, sessionAgent]);

  const lastMessageIndex = chatMessages.length - 1;
  const hasMessages = chatMessages.length > 0;

  return (
    <ChatSectionStyled className={selectedChatSources !== null ? 'sources-active' : ''}>
      <ChatColumn className="poly-chat-layout">
        <PolyChatHeader />

        <div className="text-block poly-chat-messages">
          {!hasMessages ? (
            <div className="poly-chat-empty">
              <div className="w-16 h-16 rounded-full bg-space/10 flex items-center justify-center mb-4 overflow-hidden">
                {emptyStateIcon ? (
                  <img src={emptyStateIcon} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Bot className="w-8 h-8 text-space" />
                )}
              </div>
              <h3 className="text-xl font-extrabold text-ink mb-2 tracking-tight">
                {emptyTitle}
              </h3>
              <p className="text-sm text-mute max-w-md text-center">
                {hasSession
                  ? `Your conversational interface with ${emptyTitle}. Send a message to begin.`
                  : emptyTitle
                    ? `Click New Chat to start a session with ${emptyTitle}.`
                    : 'Select an agent, then click New Chat to create a session before messaging.'}
              </p>
            </div>
          ) : (
            <div className="poly-chat-thread">
              {chatMessages?.map((elem, ind) => (
                <ChatMessageItem
                  key={`${id}-${ind}-${elem.human?.slice(0, 20) ?? ''}`}
                  elem={elem}
                  ind={ind}
                  user={user}
                  selectedAgent={selectedAgent}
                  teamDetails={teamDetails}
                  sessionAgentIcon={sessionAgentIcon}
                  selectedChatSources={selectedChatSources}
                  onSelectSources={setSelectedChatSources}
                  onSelectSource={setActiveSource}
                  onSelectEntity={setActiveEntity}
                  isLast={ind === lastMessageIndex}
                />
              ))}
            </div>
          )}
        </div>

        <div className="poly-chat-input">
          <div className="poly-chat-search">
            <PolyChatInput sessionId={id} createSession={false} />
          </div>
        </div>
      </ChatColumn>

      {selectedChatSources !== null && (
        <SearchResultCol>
          <div>
            <ul className="tablist">
              {tabs.map((tab, index) => (
                <li key={tab}>
                  <button key={index} className={tab === activeTab ? 'active' : ''} onClick={() => setActiveTab(tab)}>
                    {tab}
                  </button>
                </li>
              ))}
              <button type="button" className="source-close" title="close" onClick={() => setSelectedChatSources(null)}>
                <RxCross2 color="var(--danger)" />
              </button>
            </ul>
            <div className="tab-content">
              {activeTab === 'Sources' && <SourcesTab data={chatMessages[selectedChatSources]} />}
            </div>
          </div>
        </SearchResultCol>
      )}

      <SourceDrawer source={activeSource} onClose={() => setActiveSource(null)} />
      <EntityModal
        entity={activeEntity?.entity}
        results={activeEntity?.results}
        onClose={() => setActiveEntity(null)}
      />
    </ChatSectionStyled>
  );
};

export default ChatSection;
